import { CancelRounded } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShareIcon from '@mui/icons-material/Share';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { INVOICES_FILTER } from 'shared/constants/invoices/filters';
import Database from 'shared/db/services/Database.web';
import Invoice from 'shared/db/services/Invoice';
import { FILTER_TYPE } from 'shared/types/filter';
import { InvoiceModel } from 'shared/types/Invoice';
import { InvoicesFiltersObject } from 'shared/types/invoicesFilters';
import {
    AccountingProviderType,
    EXPORT_INVOICE_VISIBILITY,
} from 'shared/types/Invoicing';

import {
    Button,
    FilterToggleButton,
    Pagination,
    SearchInput,
    SelectableController,
    TableSkeleton,
    TooltipSyncButton,
} from '@/components';
import BackLinkButton from '@/components/BackLinkButton';
import { APP_CONTENT_ID } from '@/constants/documentIds';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { checkIfAnyFilterIsActive } from '@/helpers/filters';
import { getInitialDateRangeFilterValue } from '@/helpers/invoice/filters';
import { calculatePaginatedRows } from '@/helpers/pagination';
import {
    useDebouncedValue,
    useScrollToDocumentTopOnValueChange,
} from '@/hooks';
import {
    ExportInvoicesModal,
    FiltersPanel,
    InvoicesTable,
} from '@/pages/InvoicesPage/components';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';
import { COLOR } from '@/theme/colors';

import InvoicingNotAvailableSection from '../InvoicesPage/components/InvoicingNotAvailableSection';

const PROVIDER_NAMES: Record<AccountingProviderType, string> = {
    xero: 'Xero',
    quick_books: 'Quickbooks',
    internal: 'EQUINET Invoicing',
};

const INITIAL_FILTERS_STATE: InvoicesFiltersObject = {
    [INVOICES_FILTER.status]: [],
    [INVOICES_FILTER.date_range]: getInitialDateRangeFilterValue(),
    [INVOICES_FILTER.show_overdue]: false,
    [INVOICES_FILTER.contacts]: [],
    [INVOICES_FILTER.provider]: 'all',
};

const pageStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
};

const tableStyles: SxProps<Theme> = {
    mr: 3,
    flexGrow: 1,
};

function ArchivedInvoicesListPage() {
    const { provider } = useParams<{ provider: string }>();
    const providerType = provider as AccountingProviderType;
    const location = useLocation();
    const { ImagesService } = useImagesContext();
    const { isInitialSyncInProgress, isSyncInProgress } = useDBSyncContext();
    const { isInvoicingAvailable } = useInvoicingSystemContext();

    const totalCount =
        (location.state as { totalCount?: number })?.totalCount ?? 0;

    const [searchState, setSearchState] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [filteredInvoices, setFilteredInvoices] = useState<InvoiceModel[]>(
        [],
    );
    const [rowsCount, setRowsCount] = useState(0);
    const [page, setPage] = useState(1);
    const [notSyncedCount, setNotSyncedCount] = useState(0);
    const [showUnsyncedOnly, setShowUnsyncedOnly] = useState(false);
    const [exportPanelVisibility, setExportPanelVisibility] = useState(
        EXPORT_INVOICE_VISIBILITY.HIDDEN,
    );
    const [selectedRows, setSelectedRows] = useState<InvoiceModel[]>([]);
    const [filtersState, setFiltersState] = useState<InvoicesFiltersObject>(
        INITIAL_FILTERS_STATE,
    );
    const [isFiltersPanelVisible, setIsFiltersPanelVisible] = useState(false);

    const searchTextDebounced = useDebouncedValue(searchState);
    const filtersStateDebounced = useDebouncedValue(filtersState, 300);

    const isAnyFilterActive = useMemo(
        () =>
            checkIfAnyFilterIsActive(filtersState, [
                INVOICES_FILTER.provider,
                INVOICES_FILTER.archived,
                INVOICES_FILTER.unsynced,
            ]),
        [filtersState],
    );
    const isAnyFilterActiveDebounced = useDebouncedValue(
        isAnyFilterActive,
        300,
    );

    useScrollToDocumentTopOnValueChange(APP_CONTENT_ID, page);

    const invoiceService = useMemo(
        () =>
            new Invoice({
                database: Database.getDatabase(),
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [ImagesService],
    );

    const buildFilters = useCallback(
        (
            userFilters: InvoicesFiltersObject,
            unsynced?: boolean,
        ): InvoicesFiltersObject => ({
            ...userFilters,
            [INVOICES_FILTER.provider]: providerType,
            [INVOICES_FILTER.archived]:
                providerType !== 'internal' ? true : undefined,
            [INVOICES_FILTER.unsynced]:
                unsynced ?? (providerType !== 'internal' ? false : undefined),
        }),
        [providerType],
    );

    const fetchInvoices = useCallback(
        async (
            search: string,
            userFilters: InvoicesFiltersObject,
            unsynced?: boolean,
        ) => {
            setIsLoading(true);
            try {
                const filters = buildFilters(userFilters, unsynced);
                const count = await invoiceService.getFilteredInvoicesCount({
                    searchText: search,
                    filters,
                    filterType: FILTER_TYPE.LOKI,
                });

                setRowsCount(count);

                let paginatedInvoices: InvoiceModel[] = [];
                if (count > 0) {
                    const invoices = await invoiceService.getFilteredInvoices({
                        searchText: search,
                        filters,
                        filterType: FILTER_TYPE.LOKI,
                    });
                    paginatedInvoices = calculatePaginatedRows(page, invoices);
                }
                setFilteredInvoices(paginatedInvoices);
            } finally {
                setIsLoading(false);
            }
        },
        [buildFilters, invoiceService, page],
    );

    useEffect(() => {
        if (!isInitialSyncInProgress) {
            fetchInvoices(
                searchTextDebounced,
                filtersStateDebounced,
                showUnsyncedOnly || undefined,
            );
        }
    }, [
        fetchInvoices,
        isInitialSyncInProgress,
        searchTextDebounced,
        filtersStateDebounced,
        showUnsyncedOnly,
    ]);

    useEffect(() => setPage(1), [filtersStateDebounced, searchTextDebounced]);

    useEffect(() => {
        async function fetchNotSyncedCount() {
            if (providerType === 'internal') return;
            const details = await invoiceService.getArchivedInvoicesDetails([
                providerType,
            ]);
            const data = details[providerType];
            if (data) {
                setNotSyncedCount(data.notSynced);
            }
        }
        fetchNotSyncedCount();
    }, [invoiceService, providerType]);

    const handlePageChange = useCallback(
        (_: unknown, newPage: number) => setPage(newPage),
        [],
    );

    const handleSearchInputChange = useCallback(
        (value: string) => setSearchState(value),
        [],
    );

    const clearSearchText = useCallback(() => setSearchState(''), []);

    const handleUnsyncedBannerClick = useCallback(() => {
        setShowUnsyncedOnly((prev) => !prev);
        setPage(1);
    }, []);

    const handleFiltersChange = useCallback(
        (filterType: INVOICES_FILTER) => (value: string[]) => {
            setFiltersState((prev) => ({ ...prev, [filterType]: value }));
        },
        [],
    );

    const toggleFiltersPanel = useCallback(() => {
        setIsFiltersPanelVisible((prev) => !prev);
    }, []);

    const resetFilters = useCallback(() => {
        setFiltersState(INITIAL_FILTERS_STATE);
        setSearchState('');
    }, []);

    const onCheckboxClick = useCallback((item: InvoiceModel) => {
        setSelectedRows((prev) =>
            prev.find((r) => r.id === item.id)
                ? prev.filter((r) => r.id !== item.id)
                : [...prev, item],
        );
    }, []);

    const isCheckboxTicked = useCallback(
        (item: InvoiceModel) => !!selectedRows.find((r) => r.id === item.id),
        [selectedRows],
    );

    const selectingExportVisibility = useCallback(
        () =>
            setExportPanelVisibility(EXPORT_INVOICE_VISIBILITY.SELECTING_PHASE),
        [],
    );

    const hiddenExportVisibility = useCallback(
        () => setExportPanelVisibility(EXPORT_INVOICE_VISIBILITY.HIDDEN),
        [],
    );

    const exportingExportVisibility = useCallback(
        () =>
            setExportPanelVisibility(EXPORT_INVOICE_VISIBILITY.EXPORTING_PHASE),
        [],
    );

    const onAllSelected = useCallback(
        (items: InvoiceModel[]) => setSelectedRows(items),
        [],
    );

    const selectedInvoicesIds = useMemo(
        () => selectedRows.map((invoice) => invoice.id),
        [selectedRows],
    );

    const providerName = PROVIDER_NAMES[providerType] || providerType;
    const showLoadingState =
        (!filteredInvoices.length && isLoading) || isInitialSyncInProgress;

    const invoiceNavigationState = useMemo(
        () => ({
            fromArchived: true,
            provider: providerType,
            providerName,
            totalCount,
        }),
        [providerType, providerName, totalCount],
    );

    if (!isInvoicingAvailable) {
        return <InvoicingNotAvailableSection />;
    }

    return (
        <Box sx={pageStyles}>
            <Box sx={{ px: 3, pt: 2 }}>
                <BackLinkButton
                    text={`${t('Invoicing:archived_invoices', {
                        defaultValue: 'Archived Invoices',
                    })} (${totalCount})`}
                    to={ROUTE.archivedInvoices}
                    testID="ArchivedInvoicesListPage-BackButton"
                />
            </Box>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 4,
                        px: 3,
                        pt: 2,
                    }}
                >
                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                        <SearchInput
                            onChange={handleSearchInputChange}
                            onClear={clearSearchText}
                            testId="ArchivedInvoicesListPage-SearchInput"
                            value={searchState}
                        />
                        <FilterToggleButton
                            isActive={isAnyFilterActive}
                            isOpen={isFiltersPanelVisible}
                            onToggle={toggleFiltersPanel}
                            onClear={resetFilters}
                            testID="ArchivedInvoicesListPage-FilterButton"
                        />
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                        <TooltipSyncButton
                            tooltip={
                                isSyncInProgress
                                    ? t('AddEditModalHeader:button_tooltip', {
                                          defaultValue:
                                              'Please wait for the synchronization to complete',
                                      })
                                    : t(
                                          'SelectableController:labels:export_hover',
                                      )
                            }
                            showTooltip={
                                isSyncInProgress ||
                                exportPanelVisibility !==
                                    EXPORT_INVOICE_VISIBILITY.SELECTING_PHASE
                            }
                            tooltipPlacement="bottom"
                            disabled={
                                isSyncInProgress ||
                                exportPanelVisibility ===
                                    EXPORT_INVOICE_VISIBILITY.SELECTING_PHASE
                            }
                            onClick={selectingExportVisibility}
                            testID="ArchivedInvoicesListPage-ExportButton"
                            sx={{
                                mr: 3,
                                fontWeight: 700,
                                height: 37,
                                color: COLOR.black,
                                borderColor: COLOR.lightGray,
                                borderWidth: '1px',
                                borderRadius: '8px',
                                '&:hover': {
                                    borderColor: COLOR.lightGray,
                                    borderWidth: '1px',
                                },
                            }}
                            startIcon={<ShareIcon />}
                            variant="outlined"
                        >
                            {t('Invoicing:export')}
                        </TooltipSyncButton>
                    </Box>
                </Box>
                {isFiltersPanelVisible ? (
                    <Box sx={{ mb: 4, px: 3 }}>
                        <FiltersPanel
                            filtersState={filtersState}
                            onFiltersChange={handleFiltersChange}
                        />
                    </Box>
                ) : null}
            </Box>

            {notSyncedCount > 0 &&
                providerType !== 'internal' &&
                !showUnsyncedOnly && (
                    <Box
                        onClick={handleUnsyncedBannerClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mx: 3,
                            mb: 2,
                            px: 2,
                            py: 1.5,
                            backgroundColor: COLOR.lightWarningYellow,
                            borderRadius: '8px',
                            cursor: 'pointer',
                        }}
                    >
                        <WarningAmberIcon
                            sx={{
                                color: COLOR.warningYellow,
                                width: 20,
                                height: 20,
                            }}
                        />
                        <Typography fontWeight={700} fontSize={14}>
                            {`${t('Invoicing:not_synced', {
                                defaultValue: 'Not synced',
                            })} (${notSyncedCount})`}
                        </Typography>
                        <ArrowForwardIosIcon sx={{ width: 14, height: 14 }} />
                    </Box>
                )}

            {showUnsyncedOnly && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mx: 3,
                        mb: 2,
                    }}
                >
                    <Button
                        caption={t('Actions:showAll', {
                            defaultValue: 'Show all',
                        })}
                        onClick={handleUnsyncedBannerClick}
                        startIcon={<CancelRounded htmlColor={COLOR.paleSky} />}
                        sx={{
                            color: COLOR.paleSky,
                            fontWeight: 400,
                        }}
                    />
                </Box>
            )}

            {exportPanelVisibility ===
            EXPORT_INVOICE_VISIBILITY.SELECTING_PHASE ? (
                <Box sx={{ px: 3 }}>
                    <SelectableController
                        items={filteredInvoices}
                        selectedItems={selectedRows}
                        actionLabel={t(
                            'SelectableController:actions:invoice_export',
                        )}
                        onSelectAll={onAllSelected}
                        close={hiddenExportVisibility}
                        onActionPerformed={exportingExportVisibility}
                    />
                </Box>
            ) : null}
            <Box sx={tableStyles}>
                {showLoadingState ? (
                    <TableSkeleton />
                ) : (
                    <InvoicesTable
                        refetchInvoices={() =>
                            fetchInvoices(
                                searchTextDebounced,
                                filtersStateDebounced,
                                showUnsyncedOnly || undefined,
                            )
                        }
                        rows={filteredInvoices}
                        selectable={
                            exportPanelVisibility ===
                            EXPORT_INVOICE_VISIBILITY.SELECTING_PHASE
                        }
                        selectItem={onCheckboxClick}
                        isItemChecked={isCheckboxTicked}
                        navigationState={invoiceNavigationState}
                    />
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 3,
                    py: 2,
                }}
            >
                <Pagination
                    recordsCount={rowsCount}
                    onChange={handlePageChange}
                    page={page}
                    testID="ArchivedInvoicesListPage-Pagination"
                />
            </Box>

            {exportPanelVisibility ===
            EXPORT_INVOICE_VISIBILITY.EXPORTING_PHASE ? (
                <ExportInvoicesModal
                    close={hiddenExportVisibility}
                    invoicesIds={selectedInvoicesIds}
                    showInfoAboutFilter={
                        isAnyFilterActiveDebounced || !!searchState.length
                    }
                />
            ) : null}
        </Box>
    );
}

export default ArchivedInvoicesListPage;
